import React, { useContext, useEffect, useState } from "react";
import {
  useGetIdentity,
  useLogout,
  useTranslate,
  useList,
  useLink,
} from "@refinedev/core";
import {
  Avatar,
  Layout as AntdLayout,
  Badge,
  Button,
  Dropdown,
  Flex,
  Menu,
  theme,
} from "antd";
import {
  MenuOutlined,
  BellFilled,
  SettingOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { ColorModeContext } from "../../contexts/color-mode";
import { getInitials } from "../../shared/utils";
import { useApiUrl } from "@refinedev/core";
import { useTenantLogos, getLogoForMode } from "../../hooks/tenant";
import { OpeningStatusBadge } from "../tenant/OpeningStatusBadge";

type IUser = {
  id: number;
  name: string;
  firstName: string;
  lastName?: string;
};

type Notification = {
  id: number;
  isRead: boolean;
};

interface HeaderMobileProps {
  onMenuOpen: () => void;
}

export const HeaderMobile: React.FC<HeaderMobileProps> = ({ onMenuOpen }) => {
  const { token } = theme.useToken();
  const { data: user } = useGetIdentity<IUser>();
  const { mode } = useContext(ColorModeContext);
  const { mutate: logout } = useLogout();
  const _t = useTranslate();
  const Link = useLink();
  const apiUrl = useApiUrl();
  const { logos: tenantLogos } = useTenantLogos(apiUrl);
  const [isScrolled, setIsScrolled] = useState(false);

  const tenantLogo = getLogoForMode(tenantLogos, mode as "light" | "dark");
  const logoSrc = tenantLogo || `/images/logo-${mode}.png`;

  const { data: notificationsData, refetch } = useList<Notification>({
    resource: "notifications",
    pagination: { pageSize: 20 },
    filters: [{ field: "channel", operator: "in", value: ["in_app", "both"] }],
  });

  useEffect(() => {
    const intervalId = setInterval(() => refetch(), 120000);
    return () => clearInterval(intervalId);
  }, [refetch]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const unreadCount =
    notificationsData?.data?.filter((n) => !n.isRead).length || 0;

  const avatarItems: MenuProps["items"] = [
    {
      key: "settings",
      label: (
        <Link to="/settings">
          <SettingOutlined style={{ marginRight: 8 }} />
          {_t("settings.preferences")}
        </Link>
      ),
    },
    {
      key: "logout",
      label: (
        <a onClick={() => logout()}>
          <LogoutOutlined style={{ marginRight: 8 }} />
          {_t("buttons.logout")}
        </a>
      ),
    },
  ];

  return (
    <AntdLayout.Header
      style={{
        backgroundColor: token.colorBgLayout,
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        height: 56,
        position: "sticky",
        top: 0,
        zIndex: 1000,
        boxShadow: isScrolled ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
        transition: "box-shadow 0.3s ease",
      }}
    >
      <Flex align="center" justify="space-between" style={{ width: "100%" }}>
        {/* Left: Hamburger */}
        <Button
          type="text"
          icon={<MenuOutlined style={{ fontSize: 20 }} />}
          onClick={onMenuOpen}
          size="large"
        />

        {/* Center: Logo */}
        <Link to="/" style={{ display: "flex", alignItems: "center" }}>
          <img
            src={logoSrc}
            alt="logo"
            style={{ height: 32, objectFit: "contain" }}
          />
        </Link>

        {/* Right: Opening status + Notifications + Avatar */}
        <Flex align="center" gap={4}>
          <OpeningStatusBadge compact />

          <Link to="/notifications">
            <Badge count={unreadCount} showZero offset={[-2, 2]}>
              <Button
                type="text"
                size="large"
                icon={<BellFilled style={{ fontSize: 18 }} />}
              />
            </Badge>
          </Link>

          <Dropdown menu={{ items: avatarItems }} placement="bottomRight" arrow>
            <Button type="primary" size="middle" className="rounded-xs px-2">
              {getInitials(user?.firstName, user?.lastName)}
            </Button>
          </Dropdown>
        </Flex>
      </Flex>
    </AntdLayout.Header>
  );
};
